import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api.js';
import { readLocal, KEYS } from '../utils/storage.js';
import { friendlyError } from '../utils/errors.js';
import { parseTurnContent } from '../../shared/turnMarkers.js';

export default function History() {
  const [lookups, setLookups] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Tech name set on Home; used for the Mine-only chip so the tech can
  // narrow the list to just their own scans without typing.
  const myName = readLocal(KEYS.techName, '').trim();

  useEffect(() => {
    api.listLookups()
      .then((data) => setLookups(data.lookups))
      .catch((e) => setError(friendlyError(e, 'load your history')));
  }, [loadNonce]);

  // The list above is the 50 most recent scans. Typing searches EVERY scan
  // on the server (model, brand, tech, work order), a moment after the tech
  // stops typing; if that fails, the recent list is filtered locally instead.
  const [searchResults, setSearchResults] = useState(null);
  useEffect(() => {
    const q = filter.trim();
    if (!q) {
      setSearchResults(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.listLookups({ q, limit: 100 })
        .then((data) => { if (!cancelled) setSearchResults(data.lookups); })
        .catch(() => { if (!cancelled) setSearchResults(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [filter]);

  const source = filter.trim() && searchResults ? searchResults : lookups;
  const filtered = source?.filter((l) => {
    if (mineOnly && myName) {
      if ((l.tech_name || '').trim().toLowerCase() !== myName.toLowerCase()) {
        return false;
      }
    }
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (l.model_number || '').toLowerCase().includes(q) ||
      (l.brand_detected || '').toLowerCase().includes(q) ||
      (l.tech_name || '').toLowerCase().includes(q) ||
      (l.work_order_number || '').toLowerCase().includes(q)
    );
  });

  // Distinguish "no scans at all yet" (empty data) from "filter matched
  // nothing" (data exists, just none match) so the empty state is honest.
  const hasAnyLookups = (lookups?.length ?? 0) > 0;
  const filterIsActive = !!filter || mineOnly;

  function retryLoad() {
    setError(null);
    setLookups(null);
    setLoadNonce((n) => n + 1);
  }

  return (
    <div className="px-5 py-6 max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="h-display text-2xl text-white mb-1">History</h1>
        <p className="text-ink-400 text-sm">Recent scans across all techs — tap a row to see the troubleshooting transcript.</p>
      </div>

      {/* Filter input + clear button. The clear (×) only renders when there's
          something to clear, so it doesn't add visual noise on first load. */}
      <div className="relative">
        <input
          type="search"
          placeholder="Search model, brand, tech, work order…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-4 py-3 pr-12 bg-ink-800 border border-ink-700 rounded-lg text-white placeholder-ink-500 focus:outline-none focus:border-mra-red"
        />
        {filter && (
          <button
            type="button"
            onClick={() => setFilter('')}
            aria-label="Clear filter"
            className="absolute right-0 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-ink-400 hover:text-white"
          >
            ×
          </button>
        )}
      </div>

      {/* Mine-only chip — only shown if a tech name is set, since it filters
          by exact tech_name match. */}
      {myName && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMineOnly((v) => !v)}
            className={[
              'px-3 py-2 min-h-[44px] rounded-lg text-sm border active:scale-[0.97] transition',
              mineOnly
                ? 'bg-mra-red/15 border-mra-red text-mra-red'
                : 'bg-ink-800 border-ink-700 text-ink-200 hover:bg-ink-700',
            ].join(' ')}
          >
            {mineOnly ? '✓ Mine only' : '👤 Mine only'}
          </button>
        </div>
      )}

      {error && (
        <div className="card border-fail/30 bg-fail/5">
          <p className="text-fail-400 mb-3">⚠ {error}</p>
          <button type="button" onClick={retryLoad} className="btn-ghost text-mra-red">
            Retry
          </button>
        </div>
      )}

      {lookups === null && !error && (
        <div className="card text-center text-ink-400">Loading…</div>
      )}

      {/* Empty state — distinguish "no scans yet" from "filter matched nothing". */}
      {lookups !== null && filtered?.length === 0 && !hasAnyLookups && (
        <div className="card text-center">
          <p className="text-ink-300 mb-4">No lookups yet.</p>
          <Link to="/" className="btn-primary inline-flex">📷 Scan Your First Tag</Link>
        </div>
      )}
      {lookups !== null && filtered?.length === 0 && hasAnyLookups && filterIsActive && (
        <div className="card text-center">
          <p className="text-ink-300 mb-3">
            No scans match {filter ? <span className="font-mono">"{filter}"</span> : ''}
            {filter && mineOnly ? ' for you.' : mineOnly ? ' for you.' : '.'}
          </p>
          <button
            type="button"
            onClick={() => { setFilter(''); setMineOnly(false); }}
            className="btn-secondary"
          >
            Clear filters
          </button>
        </div>
      )}

      <div className="space-y-3">
        {filtered?.map((l) => (
          <LookupRow
            key={l.id}
            lookup={l}
            expanded={expandedId === l.id}
            onToggle={() => setExpandedId(expandedId === l.id ? null : l.id)}
          />
        ))}
      </div>
    </div>
  );
}

function LookupRow({ lookup, expanded, onToggle }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState(null);
  const [detailNonce, setDetailNonce] = useState(0);

  useEffect(() => {
    if (!expanded || detail) return;
    api.getLookup(lookup.id)
      .then(setDetail)
      .catch((e) => setDetailErr(friendlyError(e, 'load those details')));
  }, [expanded, detail, lookup.id, detailNonce]);

  function retryDetail() {
    setDetailErr(null);
    setDetailNonce((n) => n + 1);
  }

  return (
    <div className="card hover:border-ink-600 transition">
      <button
        onClick={onToggle}
        className="w-full text-left -m-1 p-1"
        aria-expanded={expanded}
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="flex-1 min-w-0">
            <div className="text-white font-mono text-lg font-bold break-all">
              {lookup.model_number}
            </div>
            <div className="text-ink-300 text-sm">{lookup.brand_detected || '—'} · {lookup.appliance_type || '—'}</div>
          </div>
          <span className="badge-source shrink-0">{formatDate(lookup.created_at)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-400">
          {lookup.tech_name && <span>👤 {lookup.tech_name}</span>}
          {lookup.work_order_number && <span className="font-mono">🧾 WO# {lookup.work_order_number}</span>}
          {lookup.serial_number && <span className="font-mono">SN {lookup.serial_number}</span>}
          <span className="text-ink-400">{expanded ? '▾ Hide details' : '▸ View details'}</span>
        </div>
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-ink-800 space-y-4">
          {detailErr && (
            <div>
              <p className="text-fail-400 text-sm mb-2">⚠ {detailErr}</p>
              <button type="button" onClick={retryDetail} className="btn-ghost text-mra-red text-sm">
                Retry
              </button>
            </div>
          )}
          {!detail && !detailErr && <p className="text-ink-400 text-sm">Loading…</p>}
          {detail && (
            <>
              {/* Back to the Result page for this unit — its docs, portal
                  link and troubleshooting — without rescanning the tag. */}
              {detail.result && (
                <button
                  type="button"
                  onClick={() => navigate('/result', { state: { result: detail.result } })}
                  className="btn-secondary w-full"
                >
                  📂 Open this job
                </button>
              )}
              {detail.documents?.length > 0 && (
                <div>
                  <h4 className="text-ink-300 text-xs uppercase tracking-wider mb-2">Documents</h4>
                  <ul className="space-y-1">
                    {detail.documents.map((d) => (
                      <li key={d.id} className="text-sm text-ink-200 flex items-center gap-2">
                        <span className="badge-source">
                          {d.source === 'upload' ? 'Uploaded' : d.source === 'scan' ? 'Scanned' : 'Portal'}
                        </span>
                        <span className="truncate">{d.file_name || `(${d.doc_type})`}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h4 className="text-ink-300 text-xs uppercase tracking-wider mb-2">
                  Troubleshooting sessions
                </h4>
                {detail.sessions?.length === 0 || !detail.sessions ? (
                  <p className="text-ink-400 text-sm">None yet.</p>
                ) : (
                  <div className="space-y-3">
                    {detail.sessions.map((s) => (
                      <SessionTranscript key={s.id} session={s} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SessionTranscript({ session }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!open || data) return;
    api.getTroubleshootSession(session.id)
      .then(setData)
      .catch((e) => setErr(friendlyError(e, 'load that transcript')));
  }, [open, data, session.id, nonce]);

  function retry() {
    setErr(null);
    setNonce((n) => n + 1);
  }

  return (
    <div className="rounded-lg border border-ink-800 bg-ink-950">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[48px] flex items-center justify-between gap-3 px-3 py-2 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="text-ink-100 text-sm truncate">{session.symptom}</div>
          <div className="text-ink-400 text-xs">{formatDate(session.created_at)}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!!session.resolved && <span className="badge-pass">Resolved</span>}
          {!!session.used_web_search && <span className="badge-source">Web</span>}
          <span className="text-ink-400 text-sm">{open ? '▾' : '▸'}</span>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-ink-800">
          {err && (
            <div>
              <p className="text-fail-400 text-sm mb-2">⚠ {err}</p>
              <button type="button" onClick={retry} className="btn-ghost text-mra-red text-sm">
                Retry
              </button>
            </div>
          )}
          {!data && !err && <p className="text-ink-400 text-sm">Loading…</p>}
          {data?.turns?.length > 0 && (
            <div className="space-y-2 mt-2">
              {data.turns.map((t) => (
                <div
                  key={t.id}
                  className={[
                    'rounded-md px-3 py-2 text-sm whitespace-pre-wrap',
                    t.role === 'ai'
                      ? 'bg-ink-900 border border-ink-800 text-ink-100'
                      : 'bg-mra-red/5 border border-mra-red/20 text-ink-100 ml-6',
                  ].join(' ')}
                >
                  <div className="text-xs uppercase tracking-wider mb-1 text-ink-400">
                    {t.role === 'ai' ? 'AI' : 'Tech'}
                  </div>
                  {/* Markers are for the app, not the tech. This rendered raw
                      content until now, so a recalled session showed literal
                      [doc:65 page:39] citations, [expect …] markers and, in one
                      real session, a 1,263-character block of <svg> XML. */}
                  {parseTurnContent(t.content).textOnly}
                  {t.role === 'tech' && t.photo_r2_key && (
                    <div className="mt-2">
                      <a
                        href={api.troubleshootPhotoUrl(t.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                      >
                        <img
                          src={api.troubleshootPhotoUrl(t.id)}
                          alt="Meter reading"
                          className="max-h-32 rounded border border-mra-red/30 object-contain bg-ink-950"
                        />
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {data?.session?.resolution_notes && (
            <div className="mt-3 text-sm text-ink-300">
              <span className="text-ink-400 text-xs uppercase tracking-wider mr-2">Notes:</span>
              {data.session.resolution_notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  const now = new Date();
  const diffMs = now - d;
  const diffH = diffMs / 3_600_000;
  if (diffH < 1) return `${Math.round(diffMs / 60000)}m ago`;
  if (diffH < 24) return `${Math.round(diffH)}h ago`;
  // Within the current calendar year: short label ("Mar 14"). Older than that:
  // include the year ("Dec 12 '25") so the tech can tell a recent entry from
  // last year's at a glance.
  const sameYear = d.getFullYear() === now.getFullYear();
  if (sameYear) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}
